import React, { useEffect, useState } from "react";
import {
  Box,
  CssBaseline,
  Paper,
  Button,
  MenuItem,
  Select,
  TextField,
  IconButton,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
// import axios from "axios";
import api from "../../api/axiosInstance"
import { toast, ToastContainer } from "react-toastify";
import AppBarComponent from "../../components/layout/AppbarComponent";
import { Delete as DeleteIcon, Save as SaveIcon, Cancel as CancelIcon } from "@mui/icons-material";
import RANKS from "../../utils/ranks";
import "react-toastify/dist/ReactToastify.css";

const MemberManagement = () => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editRowId, setEditRowId] = useState(null);
  const [editData, setEditData] = useState({});

  // --- API Calls ---
  const fetchMembers = async () => {
    setLoading(true);
    try {
      const res = await api.get(`api/member`);
      setMembers(res.data);
    } catch (err) {
      toast.error("Failed to fetch members");
    } finally {
      setLoading(false);
    }
  };

  const updateMember = async (data) => {
    try {
      await api.put(`api/member/editmember`, data);
      toast.success("Member updated successfully");
      setEditRowId(null);
      fetchMembers();
    } catch (err) {
      toast.error("Failed to update member");
    }
  };

  const deleteMember = async (user_name) => {
    if (!window.confirm("Are you sure you want to delete this member?")) return;
    try {
      await api.delete(`api/member/delmember/${user_name}`);
      toast.success("Member deleted");
      fetchMembers();
    } catch (err) {
      toast.error("Failed to delete member");
    }
  };

  // --- Handlers ---
  const handleEditRow = (row) => {
    setEditRowId(row.user_name);
    setEditData(row);
  };

  const handleCancelEdit = () => {
    setEditRowId(null);
    setEditData({});
  };

  const handleEditChange = (field, value) => {
    setEditData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveRow = () => updateMember(editData);

  useEffect(() => {
    fetchMembers();
  }, []);

  // --- DataGrid Columns ---
  const columns = [
    {
      field: "member_rank",
      headerName: "ยศ",
      flex: 1,
      renderCell: (params) =>
        editRowId === params.row.user_name ? (
          <Select
            value={editData.member_rank || ""}
            onChange={(e) => handleEditChange("member_rank", e.target.value)}
            size="small"
          >
            {RANKS.map((rank, idx) => (
              <MenuItem key={idx} value={rank}>
                {rank}
              </MenuItem>
            ))}
          </Select>
        ) : (
          params.value
        ),
    },
    {
      field: "member_name",
      headerName: "ชื่อ",
      flex: 1,
      renderCell: (params) =>
        editRowId === params.row.user_name ? (
          <TextField
            value={editData.member_name || ""}
            onChange={(e) => handleEditChange("member_name", e.target.value)}
            size="small"
          />
        ) : (
          params.value
        ),
    },
    {
      field: "member_surname",
      headerName: "สกุล",
      flex: 1,
      renderCell: (params) =>
        editRowId === params.row.user_name ? (
          <TextField
            value={editData.member_surname || ""}
            onChange={(e) =>
              handleEditChange("member_surname", e.target.value)
            }
            size="small"
          />
        ) : (
          params.value
        ),
    },
    {
      field: "member_position",
      headerName: "ตำแหน่ง",
      flex: 2,
      renderCell: (params) =>
        editRowId === params.row.user_name ? (
          <TextField
            value={editData.member_position || ""}
            onChange={(e) =>
              handleEditChange("member_position", e.target.value)
            }
            size="small"
          />
        ) : (
          params.value
        ),
    },
    {
      field: "user_name",
      headerName: "Username",
      flex: 1,
    },
    {
      field: "member_right",
      headerName: "กำหนดสิทธิ์",
      flex: 1,
      renderCell: (params) =>
        editRowId === params.row.user_name ? (
          <Select
            value={editData.member_right || ""}
            onChange={(e) => handleEditChange("member_right", e.target.value)}
            size="small"
          >
            <MenuItem value="pending">รออนุมัติ</MenuItem>
            <MenuItem value="user">User</MenuItem>
            <MenuItem value="admin">Admin</MenuItem>
          </Select>
        ) : (
          <Button
            size="small"
            variant="outlined"
            onClick={() => handleEditRow(params.row)}
          >
            {params.value === "pending" ? "รออนุมัติ" : params.value}
          </Button>
        ),
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        const isEditing = editRowId === params.row.user_name;
        return isEditing ? (
          <>
            <IconButton color="primary" onClick={handleSaveRow}>
              <SaveIcon />
            </IconButton>
            <IconButton color="secondary" onClick={handleCancelEdit}>
              <CancelIcon />
            </IconButton>
          </>
        ) : (
          <IconButton
            color="error"
            onClick={() => deleteMember(params.row.user_name)}
          >
            <DeleteIcon />
          </IconButton>
        );
      },
    },
  ];

  return (
    <>
      <CssBaseline />
      <AppBarComponent />
      <Box
        sx={{
          height: "calc(100vh - 110px)",
          width: "100vw",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: "#ffffff",
          p: 2,
        }}
      >
        <Paper
          elevation={3}
          sx={{
            width: { xs: "95%", sm: "90%", md: "90%" },
            height: "80%",
            borderRadius: 2.5,
            backgroundColor: "#C8EAE6",
            border: "2px solid #000000",
            boxShadow: "0 4px 10px rgba(0,0,0,0.5)",
            p: 2,
          }}
        >
          <DataGrid
            rows={members.map((m) => ({ id: m.user_name, ...m }))}
            columns={columns}
            pageSize={10}
            rowsPerPageOptions={[10, 20]}
            loading={loading}
            autoHeight
          />
        </Paper>
      </Box>
      <ToastContainer position="top-right" autoClose={3000} />
    </>
  );
};

export default MemberManagement;
